import { AdminMembersReportEventsTableRowFragment$key } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTableRowFragment.graphql"
import OccurrenceNameAndTime from "@/product/course/event/components/OccurrenceNameAndTime"
import useUserTimezone from "@/user/util/useUserTimezone"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import DaySquare from "@components/date/day-square/DaySquare"
import { DiscoIcon, DiscoTable, DiscoText, DiscoTooltip } from "@disco-ui"
import { TableCell } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { formatInTimeZone } from "date-fns-tz"
import { graphql, useFragment } from "react-relay"

interface Props {
  occurrenceKey: AdminMembersReportEventsTableRowFragment$key
}

function AdminMembersReportEventsTableRow({ occurrenceKey }: Props) {
  const authUserTimezone = useUserTimezone()
  const isMobile = useIsMobile()
  const classes = useStyles()
  const occurrence = useFragment<AdminMembersReportEventsTableRowFragment$key>(
    graphql`
      fragment AdminMembersReportEventsTableRowFragment on Occurrence
      @argumentDefinitions(organizationMembershipId: { type: "ID!" }) {
        ...OccurrenceNameAndTimeFragment
        name
        datetimeRange
        product {
          name
        }
        app {
          customAppTitle
        }
        recording {
          id
        }
        organizationMembershipRSVP(organizationMembershipId: $organizationMembershipId) {
          id
        }
        organizationMembershipAttendance(
          organizationMembershipId: $organizationMembershipId
        ) {
          attendedAt
        }
        organizationMembershipFeedback(
          organizationMembershipId: $organizationMembershipId
        ) {
          instructorRating
          videoQualityRating
          contentRating
          comments
        }
        organizationMembershipRecordingVideoProgress(
          organizationMembershipId: $organizationMembershipId
        ) {
          maxPercent
        }
      }
    `,
    occurrenceKey
  )

  const now = new Date()
  const startTime = new Date(occurrence.datetimeRange[0])
  const isUpcoming = now.getTime() <= startTime.getTime()

  return (
    <DiscoTable.Row testid={`AdminMembersReportEventsTableRow.${occurrence.name}`}>
      {/* Name */}
      <TableCell>
        <div className={classes.nameContainer}>
          {!isMobile && <DaySquare testid={"list-event-day"} date={startTime} />}
          <OccurrenceNameAndTime occurrenceKey={occurrence} variant={"compact"} />
        </div>
      </TableCell>

      {/* Event Location */}
      <TableCell>
        <DiscoText variant={"body-sm"}>
          {occurrence.app ? occurrence.app.customAppTitle : occurrence.product.name}
        </DiscoText>
      </TableCell>

      {/* Status */}
      <TableCell>{isUpcoming ? "Upcoming" : "Past"}</TableCell>

      {/* Confirmed */}
      <TableCell>{occurrence.organizationMembershipRSVP ? "Yes" : "No"}</TableCell>

      {/* Attended */}
      <TableCell>
        {isUpcoming ? "-" : occurrence.organizationMembershipAttendance ? "Yes" : "No"}
      </TableCell>

      {/* Attendance Time */}
      <TableCell>
        {occurrence.organizationMembershipAttendance
          ? formatInTimeZone(
              occurrence.organizationMembershipAttendance.attendedAt,
              authUserTimezone,
              DATE_FORMAT.LONG_TIME_FORMAT
            )
          : "-"}
      </TableCell>

      {/* Feedback */}
      <TableCell className={classes.feedbackTableCell}>
        {occurrence.organizationMembershipFeedback ? (
          <div className={classes.feedbackContainer}>
            {(
              (occurrence.organizationMembershipFeedback.instructorRating +
                occurrence.organizationMembershipFeedback.videoQualityRating +
                occurrence.organizationMembershipFeedback.contentRating) /
              3
            ).toFixed(1)}
            <DiscoIcon className={classes.feedbackStarIcon} icon={"star"} active />
            {occurrence.organizationMembershipFeedback.comments && (
              <DiscoTooltip content={occurrence.organizationMembershipFeedback.comments}>
                <span className={classes.feedbackCommentsIconContainer}>
                  <DiscoIcon icon={"message"} width={16} height={16} />
                </span>
              </DiscoTooltip>
            )}
          </div>
        ) : (
          "-"
        )}
      </TableCell>

      {/* Viewed Recording */}
      <TableCell>
        {occurrence.recording
          ? occurrence.organizationMembershipRecordingVideoProgress
            ? "Yes"
            : "No"
          : "-"}
      </TableCell>

      {/* Watch Progress */}
      <TableCell>
        {occurrence.recording
          ? occurrence.organizationMembershipRecordingVideoProgress
            ? `${Math.round(
                occurrence.organizationMembershipRecordingVideoProgress.maxPercent * 100
              )}%`
            : "-"
          : "-"}
      </TableCell>
    </DiscoTable.Row>
  )
}

const useStyles = makeUseStyles((theme) => ({
  nameContainer: {
    display: "flex",
    minWidth: "300px",
  },
  feedbackTableCell: {
    textAlign: "left",
  },
  feedbackContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  feedbackStarIcon: {
    width: "12px",
    height: "12px",
    color: theme.palette.groovy.yellow[400],
    "& svg": {
      color: theme.palette.groovy.yellow[400],
    },
  },
  feedbackCommentsIconContainer: {
    display: "flex",
  },
}))

export function AdminMembersReportEventsTableRowSkeleton() {
  return (
    <DiscoTable.Row>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
    </DiscoTable.Row>
  )
}

export default AdminMembersReportEventsTableRow
